/**
 * Notification System Types
 *
 * TypeScript interfaces for the notification system Redux state.
 */

// ============================================================================
// Notification Categories & Priorities
// ============================================================================

export type NotificationCategory =
  | 'system'              // Platform announcements, maintenance
  | 'call'                // Incoming, missed, ended calls
  | 'report'              // Report generation, approvals
  | 'action_required'     // Supervisor approvals, pending reviews
  | 'comment'             // Comments on reports/cases
  | 'mention'             // @mentions in chat/notes
  | 'alert'               // Security alerts, thresholds
  | 'ai_suggestion'       // AI-generated suggestions
  | 'ai_analysis_complete' // AI analysis finished
  | 'achievement';         // Achievement unlocks and training milestones

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export type NotificationBehavior = 'informational' | 'actionable';

export type DigestMode = 'realtime' | 'hourly' | 'daily' | 'weekly';

// ============================================================================
// Core Notification Model
// ============================================================================

export interface Notification {
  id: string;
  userId: string;
  orgUnitId?: string;

  // Classification
  category: NotificationCategory;
  priority: NotificationPriority;
  behavior: NotificationBehavior;

  // Content
  title: string;
  body: string;
  icon?: string;              // Lucide icon name
  actionUrl?: string;         // URL to navigate on click
  actionLabel?: string;       // CTA button text

  // Flexible payload
  metadata: Record<string, unknown>;

  // Page context
  sourcePage?: string | null; // Which page this notification relates to (e.g., '/calls', '/reports')
                              // null = system-wide notification that shows on all pages

  // State
  isRead: boolean;
  readAt?: string;            // ISO timestamp
  isArchived: boolean;

  // Delivery tracking
  deliveredInapp: boolean;
  deliveredPush: boolean;
  deliveredEmail: boolean;

  // Timestamps
  createdAt: string;          // ISO timestamp
  expiresAt?: string;         // ISO timestamp
}

// ============================================================================
// User Preferences
// ============================================================================

export interface NotificationPreference {
  category: NotificationCategory | '_all';

  // Master enable
  enabled: boolean;

  // Per-channel enables
  inappEnabled: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;

  // Delivery preferences
  digestMode: DigestMode;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;   // HH:MM format
  quietHoursEnd?: string;     // HH:MM format

  // Sound preferences
  soundEnabled: boolean;
  soundId: string;            // 'default', 'alert', 'subtle', etc.

  // Priority filtering
  minPriority: NotificationPriority;
}

// ============================================================================
// Push Subscription
// ============================================================================

export interface PushSubscription {
  id: string;
  endpoint: string;
  deviceName?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  isActive: boolean;
  createdAt: string;
  lastSuccessAt?: string;
}

// ============================================================================
// Redux State
// ============================================================================

export interface NotificationsState {
  // Data
  notifications: Notification[];
  unreadCount: number;
  unreadByCategory: Record<string, number>;
  preferences: NotificationPreference[];
  pushSubscriptions: PushSubscription[];

  // UI State
  isDropdownOpen: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  activeFilter: NotificationCategory | 'all';

  // Push State
  pushSupported: boolean;
  pushPermission: NotificationPermission;
  pushSubscribed: boolean;

  // Pagination
  currentPage: number;
  totalCount: number;

  // Error State
  error: string | null;
  lastFetchedAt: string | null;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  hasMore: boolean;
}

export interface UnreadCountResponse {
  total: number;
  byCategory: Record<string, number>;
  /** @deprecated Use byCategory - this is for snake_case API compatibility */
  by_category?: Record<string, number>;
}

export interface BulkActionResponse {
  updatedCount: number;
  success: boolean;
}

// ============================================================================
// Action Payloads
// ============================================================================

export interface FetchNotificationsPayload {
  limit?: number;
  offset?: number;
  category?: NotificationCategory;
  isRead?: boolean;
  priority?: NotificationPriority;
  includeArchived?: boolean;
  /** Filter by page path (e.g., '/calls', '/reports') */
  sourcePage?: string;
  /** Include system notifications (source_page IS NULL) */
  includeSystem?: boolean;
  /** Include high/urgent priority notifications regardless of page */
  minPriority?: 'high' | 'urgent';
}

export interface CreateNotificationPayload {
  userId: string;
  orgUnitId?: string;
  category: NotificationCategory;
  priority?: NotificationPriority;
  behavior?: NotificationBehavior;
  title: string;
  body: string;
  icon?: string;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  expiresAt?: string;
  /** Page path this notification relates to (null = system-wide) */
  sourcePage?: string | null;
}

export interface UpdatePreferencePayload {
  category: NotificationCategory | '_all';
  enabled?: boolean;
  inappEnabled?: boolean;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  digestMode?: DigestMode;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  soundEnabled?: boolean;
  soundId?: string;
  minPriority?: NotificationPriority;
}

export interface RegisterPushSubscriptionPayload {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  deviceName?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
}

// ============================================================================
// Category Metadata (for UI display)
// ============================================================================

export interface CategoryMeta {
  id: NotificationCategory;
  label: string;
  icon: string;
  color: string;
  description: string;
}

export const NOTIFICATION_CATEGORIES: CategoryMeta[] = [
  {
    id: 'system',
    label: 'System',
    icon: 'Info',
    color: 'cyan',
    description: 'Platform announcements and maintenance',
  },
  {
    id: 'call',
    label: 'Calls',
    icon: 'Phone',
    color: 'amber',
    description: 'Incoming, missed, and ended calls',
  },
  {
    id: 'report',
    label: 'Reports',
    icon: 'FileText',
    color: 'emerald',
    description: 'Report generation and approvals',
  },
  {
    id: 'action_required',
    label: 'Action Required',
    icon: 'AlertTriangle',
    color: 'rose',
    description: 'Pending reviews and supervisor approvals',
  },
  {
    id: 'comment',
    label: 'Comments',
    icon: 'MessageSquare',
    color: 'blue',
    description: 'Comments on reports and cases',
  },
  {
    id: 'mention',
    label: 'Mentions',
    icon: 'AtSign',
    color: 'purple',
    description: '@mentions in chat and notes',
  },
  {
    id: 'alert',
    label: 'Alerts',
    icon: 'AlertCircle',
    color: 'red',
    description: 'Security alerts and threshold breaches',
  },
  {
    id: 'ai_suggestion',
    label: 'Anderra Suggestions',
    icon: 'Sparkles',
    color: 'violet',
    description: 'AI-generated suggestions and recommendations',
  },
  {
    id: 'ai_analysis_complete',
    label: 'AI Analysis',
    icon: 'CheckCircle2',
    color: 'green',
    description: 'AI analysis completed notifications',
  },
  {
    id: 'achievement',
    label: 'Achievements',
    icon: 'Trophy',
    color: 'amber',
    description: 'Achievement unlocks and training milestones',
  },
];

export const getCategoryMeta = (category: NotificationCategory): CategoryMeta | undefined => {
  return NOTIFICATION_CATEGORIES.find((c) => c.id === category);
};

// ============================================================================
// Sound Options
// ============================================================================

export const NOTIFICATION_SOUNDS = [
  { id: 'default', label: 'Default', file: '/notification-sounds/default.mp3' },
  { id: 'alert', label: 'Alert', file: '/notification-sounds/alert.mp3' },
  { id: 'subtle', label: 'Subtle', file: '/notification-sounds/subtle.mp3' },
  { id: 'none', label: 'None (Silent)', file: null },
];

// ============================================================================
// Page Label Mapping (for source_page display)
// ============================================================================

export interface PageMeta {
  path: string;
  label: string;
  icon: string;
}

/**
 * Map of page paths to friendly display labels and icons
 */
export const PAGE_LABELS: Record<string, PageMeta> = {
  '/calls': { path: '/calls', label: 'Calls', icon: 'Phone' },
  '/reports': { path: '/reports', label: 'Reports', icon: 'FileText' },
  '/files': { path: '/files', label: 'Files', icon: 'FolderOpen' },
  '/knowledge-base': { path: '/knowledge-base', label: 'Knowledge Base', icon: 'BookOpen' },
  '/dashboard': { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
  '/settings': { path: '/settings', label: 'Settings', icon: 'Settings' },
  '/history': { path: '/history', label: 'History', icon: 'Clock' },
  '/developer': { path: '/developer', label: 'Developer', icon: 'Code' },
  '/adminpanel': { path: '/adminpanel', label: 'Admin', icon: 'Shield' },
};

/**
 * Get friendly page label from path
 * Returns the path segment if no mapping found
 */
export const getPageLabel = (path: string | null | undefined): string => {
  if (!path) return 'System';

  // Check exact match first
  if (PAGE_LABELS[path]) {
    return PAGE_LABELS[path].label;
  }

  // Check for prefix match (e.g., '/calls/123' → 'Calls')
  const matchedKey = Object.keys(PAGE_LABELS).find(key => path.startsWith(key));
  if (matchedKey) {
    return PAGE_LABELS[matchedKey].label;
  }

  // Fallback: capitalize first path segment
  const segment = path.split('/').filter(Boolean)[0] || 'Unknown';
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
};

/**
 * Get page icon from path
 */
export const getPageIcon = (path: string | null | undefined): string => {
  if (!path) return 'Globe';

  // Check exact match first
  if (PAGE_LABELS[path]) {
    return PAGE_LABELS[path].icon;
  }

  // Check for prefix match
  const matchedKey = Object.keys(PAGE_LABELS).find(key => path.startsWith(key));
  if (matchedKey) {
    return PAGE_LABELS[matchedKey].icon;
  }

  return 'Link';
};
